/* Iter 31.128 — Performer overlay (animate agents during espeak readout).
 *
 * Per One's directive (30 May 2026): animate the agent's portrait while
 * espeak reads — the agent becomes a performer. Designed for the
 * Foundry generate readback first; reusable for the Radio bulletin
 * stream second.
 *
 * Animation pattern:
 *   - jaw scaleY tick on every Web Speech `boundary` event (word/sentence)
 *   - subtle 1.2s head-bob loop while reading
 *   - brightness lift to feel "live"
 *   - data-mouth toggles between two CSS states (open/closed) so we
 *     stay declarative rather than inline-style-thrash
 *
 * No external animation library — pure CSS keyframes + state ticks.
 * Espeak doesn't expose an audio source for amplitude analysis, so the
 * head-bob is a fixed sine; for music-driven bopping (Radio), we'll
 * add a separate AudioContext-driven variant in a follow-up iter.
 */
import React, { useEffect, useRef, useState } from "react";

// Iter 31.132 — Web Audio amplitude analyser is one-shot per audio
// element across the app. Calling createMediaElementSource twice on
// the same node throws "InvalidStateError"; we memoise the result.
const _audioGraphCache = new WeakMap();

function getOrBuildAnalyser(audioElement) {
    if (!audioElement) return null;
    const cached = _audioGraphCache.get(audioElement);
    if (cached) return cached;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try {
        const ctx = new AC();
        const source = ctx.createMediaElementSource(audioElement);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.6;
        // Keep audio audible — fork analyser off the main path.
        source.connect(analyser);
        analyser.connect(ctx.destination);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const graph = { ctx, source, analyser, data };
        _audioGraphCache.set(audioElement, graph);
        return graph;
    } catch (exc) {
        // createMediaElementSource throws if already wired by some other
        // path — caller falls back to non-amplitude bopping.
        // eslint-disable-next-line no-console
        console.warn("PerformerOverlay: analyser setup skipped:", exc?.message);
        return null;
    }
}

export const PerformerOverlay = ({
    imageUrl,
    text,
    playKey = 0,         // increment to re-trigger readout
    muted = false,
    rate = 0.92,
    pitch = 1.0,
    volume = 0.9,
    audioElement = null,   // iter 31.132 — when set, drives amplitude bop
    audioActive = false,   // when true AND audioElement set, runs analyser
    forceReading = false,  // iter 31.132c — force is-reading visual state
                           // without triggering speech; pairs with a faux
                           // jaw-tick interval so the portrait still
                           // animates while an external TTS path speaks.
    visemeTrack = null,    // iter 31.138 — cached word-level alignment
                           // (array of {word, start_ms, end_ms}). When
                           // present AND audioElement is playing, the
                           // mouthTick advances on word-boundary
                           // crossings instead of 5Hz tick OR amplitude.
                           // Real lip-sync from forced alignment cache.
    onDone,
    className = "",
    altText = "agent portrait",
}) => {
    const [isReading, setIsReading] = useState(false);
    const [mouthTick, setMouthTick] = useState(0);
    const utteranceRef = useRef(null);
    const overlayRef = useRef(null);
    const rafRef = useRef(null);
    // Iter 31.138 — cursor for viseme-driven mouth ticks. Walks the
    // cached viseme_track in O(1) amortised per frame.
    const visemeCursorRef = useRef(0);

    // Iter 31.132c — faux jaw-tick when an EXTERNAL TTS path drives the
    // speech (Radio.jsx's `speak()` for drops, or RadioProvider's
    // bulletin). PerformerOverlay can't subscribe to onboundary events
    // it didn't fire, so we drive a steady ~5Hz mouth flicker. Reads
    // less precise than real boundary syncing but keeps the portrait
    // alive on screen while another voice speaks.
    useEffect(() => {
        if (!forceReading) return;
        const id = setInterval(() => setMouthTick((t) => t + 1), 200);
        return () => clearInterval(id);
    }, [forceReading]);

    // Iter 31.132 — music-driven amplitude bop. When ``audioElement``
    // is provided AND ``audioActive`` flips true (music playing), the
    // Web Audio analyser samples FFT data on every frame, computes
    // an RMS-ish amplitude, and writes it as a CSS custom property
    // ``--bop-amp`` on the overlay root. The keyframe consumes that
    // variable so the head-bob is gain-aware rather than a fixed sine.
    useEffect(() => {
        if (!audioElement || !audioActive) {
            // Stop any prior loop + reset to baseline.
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            if (overlayRef.current) overlayRef.current.style.setProperty("--bop-amp", "0");
            return;
        }
        const graph = getOrBuildAnalyser(audioElement);
        if (!graph) return;
        // Resume context if it was suspended (Safari/Chrome autoplay rules).
        if (graph.ctx.state === "suspended") {
            graph.ctx.resume().catch(() => {});
        }
        const tick = () => {
            graph.analyser.getByteFrequencyData(graph.data);
            // Take the low-mids only — kicks/bass drive the bop best.
            const slice = graph.data.slice(2, 16);
            let sum = 0;
            for (let i = 0; i < slice.length; i++) sum += slice[i];
            const avg = sum / slice.length / 255; // 0..1
            // Smooth and clamp.
            const amp = Math.min(1, Math.max(0, avg * 1.4));
            if (overlayRef.current) {
                overlayRef.current.style.setProperty("--bop-amp", amp.toFixed(3));
            }
            // Iter 31.138 — viseme-driven mouth tick. When the playing
            // track has a cached viseme_track, drive the mouth open
            // by walking the array against audioElement.currentTime.
            // The boundary-crossing scheme avoids per-frame string
            // mutation: we keep a cursor index in the ref and only
            // bump mouthTick when we cross a new word.
            if (visemeTrack && visemeTrack.length && audioElement) {
                const tMs = (audioElement.currentTime || 0) * 1000;
                let cursor = visemeCursorRef.current;
                // Re-find cursor if user seeked backwards.
                if (cursor >= visemeTrack.length ||
                    (cursor > 0 && visemeTrack[cursor - 1].start_ms > tMs)) {
                    cursor = 0;
                }
                while (cursor < visemeTrack.length &&
                       visemeTrack[cursor].end_ms < tMs) {
                    cursor += 1;
                }
                if (cursor !== visemeCursorRef.current) {
                    visemeCursorRef.current = cursor;
                    setMouthTick((t) => t + 1);
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [audioElement, audioActive, visemeTrack]);

    useEffect(() => {
        if (!text || playKey === 0) return;
        if (muted || !("speechSynthesis" in window)) {
            onDone?.();
            return;
        }
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(text);
            u.rate = rate;
            u.pitch = pitch;
            u.volume = volume;
            u.onstart = () => setIsReading(true);
            u.onboundary = () => setMouthTick((t) => t + 1);
            u.onend = () => { setIsReading(false); onDone?.(); };
            u.onerror = () => { setIsReading(false); onDone?.(); };
            utteranceRef.current = u;
            window.speechSynthesis.speak(u);
        } catch (_) {
            setIsReading(false);
            onDone?.();
        }
        return () => {
            try { window.speechSynthesis.cancel(); } catch (_) { /* ignore */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playKey, muted]);

    if (!imageUrl) return null;
    const readingClass = (isReading || forceReading) ? "is-reading" : "";
    return (
        <div
            ref={overlayRef}
            className={`performer-overlay ${readingClass} ${audioActive ? "is-bopping" : ""} ${className}`}
            data-mouth={mouthTick % 2}
            data-testid="performer-overlay"
        >
            <img
                src={imageUrl}
                alt={altText}
                className="performer-portrait"
                data-testid="performer-portrait-img"
            />
            {(isReading || forceReading) && (
                <span className="performer-on-air" data-testid="performer-on-air">
                    ON AIR
                </span>
            )}
        </div>
    );
};

export default PerformerOverlay;
